// 9fbef606107a605d69c0edbcd8029e5d
import { ApolloLink } from 'apollo-link';
import { isServer, isClient, shouldUpdateGraphqlCache, parseBoolean } from '@tcp/core/src/utils';
import logger from '@tcp/core/src/utils/loggerInstance';
import { USID_DUP, USID_EXP } from '@tcp/core/src/services/api.constants';
import { setLocalStorage, getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import { SESSIONCONFIG_REDUCER_KEY } from '@tcp/core/src/constants/reducer.constants';
import { getStoreRef } from '@tcp/core/src/utils/store.utils';
import { FETCH_POLICY } from './ApolloGraphQLConstants';

export const getapiTimeout = (serverTimeout, clientTimeout) => {
  const apiConfigVal = isClient() ? clientTimeout : serverTimeout;
  return apiConfigVal || 30000;
};

export const getClientOptions = (apiConfigObj) => {
  const {
    graphql_endpoint_url_server: graphqlEndpointServer,
    graphql_endpoint_url: graphqlEndpointClient,
    graphql_api_key: graphqlApiKey,
    graphql_region: graphqlRegion,
    graphql_auth_type: graphqlAuthType,
  } = apiConfigObj;

  const graphQLEndpoint = isServer() ? graphqlEndpointServer : graphqlEndpointClient;
  logger.info(`GraphQL Endpoint Stateless: ${graphQLEndpoint} ---- ${graphqlApiKey}`);

  return {
    url: graphQLEndpoint,
    region: graphqlRegion,
    auth: {
      type: graphqlAuthType,
      apiKey: graphqlApiKey,
    },
    disableOffline: true,
  };
};

export const getStatefulClientOptions = (apiConfigObj) => {
  const {
    graphql_stateful_endpoint_url: graphqlStatefulEndpointClient,
    graphql_stateful_endpoint_url_ssr: graphqlStatefulEndpointServer,
  } = apiConfigObj;
  logger.info(`GraphQL Endpoint Stateful Client: ${graphqlStatefulEndpointClient}`);
  logger.info(`GraphQL Endpoint Stateful Server ${graphqlStatefulEndpointServer}`);

  return {
    url: isServer() ? graphqlStatefulEndpointServer : graphqlStatefulEndpointClient,
    disableOffline: true,
  };
};

export const getGraphQLErrorOnSuccess = (res) => {
  return res.errors.map((e) => ({
    errorMessage: e && e.message,
    errorCode: e && e.extensions && e.extensions.errorCode,
    errorParameters: e && e.extensions && e.extensions.errorParameters,
  }));
};

export const getGraphQLErrorOnError = (err) => {
  const { graphQLErrors = [], networkError } = err;
  if (networkError) {
    return [
      {
        errorMessage: networkError.message,
        errorCode: 'ERR_SOMETHING_WRONG',
      },
    ];
  }
  return graphQLErrors.map((e) => ({
    errorMessage: e.message,
    errorCode: 'ERR_SOMETHING_WRONG',
  }));
};

export const getFetchPolicy = ({ type, cacheConfigKey = '', TTL = 0 }) => {
  let updatedFetchPolicy = '';

  if (isServer() || type === 'mutation') {
    updatedFetchPolicy = 'no-cache';
  } else if (shouldUpdateGraphqlCache(TTL, cacheConfigKey)) {
    updatedFetchPolicy = FETCH_POLICY.NETWORK_ONLY;
  } else {
    updatedFetchPolicy = 'cache-first';
  }

  return updatedFetchPolicy;
};

export const getIsPassUsidInHeaderEnabled = () => {
  const storeRef = getStoreRef();
  const value =
    (storeRef && storeRef.getState()[SESSIONCONFIG_REDUCER_KEY])?.siteDetails
      ?.PASS_USID_IN_HEADERS || '0';
  return parseBoolean(value);
};

export const getDataFromStorage = async (cacheKey) => {
  let value;
  if (isClient() && window.localStorage) {
    value = getLocalStorage(cacheKey);
  }
  return value;
};

export const setDataInStorage = async (cacheKey, data) => {
  try {
    if (isClient()) {
      setLocalStorage({ key: cacheKey, value: data });
    }
  } catch (e) {
    return true;
  }
  return true;
};

export const setUsidInStorage = (usidHeader) => {
  try {
    const usid = usidHeader && usidHeader.trim && usidHeader.trim();
    if (usid) {
      setDataInStorage(USID_DUP, usid);
    }
  } catch (e) {
    return true;
  }
  return true;
};

export const setUsidExpiryInStorage = (usidHeader) => {
  try {
    const usidExp = usidHeader && usidHeader.trim && usidHeader.trim();
    if (usidExp) {
      const expirationTime = new Date().getTime() + usidExp * 24 * 60 * 60 * 1000;
      setDataInStorage(USID_EXP, expirationTime);
    }
  } catch (e) {
    return true;
  }
  return true;
};

export const afterResponseLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    const usidHeader = context?.response?.headers?.get(USID_DUP);
    setUsidInStorage(usidHeader);
    const usidHeaderExp = context?.response?.headers?.get(USID_EXP);
    setUsidExpiryInStorage(usidHeaderExp);
    return response;
  });
});
